import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { allFocusOptions, opinionCommissionedByOptions, opinionScopeOptions } from '../../../constants/opinion';
import { Opinion } from '../../../shared/icons';
import { ConfirmationModal } from '../../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { ArkTable } from '../../../shared/table/ArkTable';
import { bespokeFormatter, commissionedByFormatter, dateFormatter, scopeFormatter } from '../../../shared/table/arkTableFormatters';
import { TableFilterType, ColumnSort, ArkTableColumn } from '../../../shared/table/types';
import { sourceOpinionsPaginationNext, sourceOpinionsPaginationPrevious, setSourceOpinionsTableFilters, clearSourceOpinionsTableFilters, setSourceOpinionsTableColumnSort, setSourceOpinionsPageSize, ArkOpinion } from '../store';
import { getSourceOpinionsColumnSort, getSourceOpinionsFilters, getSourceOpinionsPageSize, getSourceOpinionsPageNumber, getSourceOpinionsModalOpen, getSourceOpinionsIsLoading, getTotalSourceOpinions } from '../store/selectors';

interface OpinionLinkModalProps {
    select: (value: number) => void;
    deselect: (value: number) => void;
    selectDisabled: (value: number) => boolean;
    close: () => void;
    sourceOpinionIds: number[] | undefined;
    sourceOpinions: ArkOpinion[];
}

export const OpinionLinkModal: React.FC<OpinionLinkModalProps> = ({ select, close, sourceOpinionIds, sourceOpinions, deselect, selectDisabled }) => {
    const dispatch = useAppDispatch();

    const isOpen = useAppSelector(getSourceOpinionsModalOpen);
    const isLoading = useAppSelector(getSourceOpinionsIsLoading);
    const pageNumber = useAppSelector(getSourceOpinionsPageNumber);
    const total = useAppSelector(getTotalSourceOpinions);
    const filters = useAppSelector(getSourceOpinionsFilters);
    const columnSort = useAppSelector(getSourceOpinionsColumnSort);
    const pageSize = useAppSelector(getSourceOpinionsPageSize);

    const nextPage = useCallback(() => dispatch(sourceOpinionsPaginationNext()), [dispatch]);
    const previousPage = useCallback(() => dispatch(sourceOpinionsPaginationPrevious()), [dispatch]);
    const updateFilter = useCallback((key: string, value: string | string[] | null, type: keyof TableFilterType = 'text') => dispatch(setSourceOpinionsTableFilters(key, value, type)), [dispatch]);
    const clearAllFilters = useCallback(() => dispatch(clearSourceOpinionsTableFilters()), [dispatch]);
    const toggleColumnSort = useCallback((columnSort: ColumnSort) => dispatch(setSourceOpinionsTableColumnSort(columnSort)), [dispatch]);
    const updatePageSize = useCallback((pageSize: number) => dispatch(setSourceOpinionsPageSize(pageSize)), [dispatch]);

    const selectedIds = useMemo(() => sourceOpinionIds ? sourceOpinionIds : null, [sourceOpinionIds]);

    const selectId = useCallback((selectedId: number | string) => select(selectedId as number), [select]);

    const deselectId = useCallback((selectedId: number | string) => deselect(selectedId as number), [deselect]);

    const selectedDisabled = useCallback((selectedId: number) => selectDisabled(selectedId), [selectDisabled]);

    const colDefs: ArkTableColumn[] = [
        { id: 'opinionJurisdiction', header: 'Jurisdiction', field: 'jurisdiction', width: 0.20 },
        { id: 'focus', header: 'Focus', field: 'focus', width: 0.10, canFilter: true, component: 'tooltip', canSort: true },
        { id: 'bespoke', header: 'Bespoke/Industry Standard', field: 'bespoke', width: 0.20, valueFormatter: bespokeFormatter, canFilter: true, canSort: true },
        { id: 'scope', header: 'Scope', field: 'scope', width: 0.15, component: 'tooltip', canFilter: true, valueFormatter: scopeFormatter, canSort: true },
        { id: 'commissionedBy', header: 'Commissioned By', field: 'commissionedBy', width: 0.15, valueFormatter: commissionedByFormatter, component: 'tooltip', canFilter: true, canSort: true },
        { id: 'dateOfOpinion', header: 'Date of Opinion', field: 'dateOfOpinion', width: 0.15, valueFormatter: dateFormatter, canSort: true },
        { id: 'selectCell', header: '', field: 'opinionId', width: 0.05, component: 'select', select: selectId, selectedIds: selectedIds, deselect: deselectId, selectDisabled: selectedDisabled }
    ];

    const bespokeOptions = useMemo(() => [{ label: 'Bespoke', value: '1' }, { label: 'Industry Standard', value: '0' }], []);

    const scopeOptions = useMemo(() => opinionScopeOptions(), []);
    const commissionedByOptions = useMemo(() => opinionCommissionedByOptions(), []);

    const filterDropdownOptions = useMemo(() => ({
        scope: scopeOptions,
        bespoke: bespokeOptions,
        commissionedBy: commissionedByOptions,
        focus: allFocusOptions
    }), [bespokeOptions, scopeOptions, commissionedByOptions]);

    const emptyTableMessage = useMemo(() => sourceOpinions.length === 0 ? 'There are no potential source opinions for the selected jurisdiction.' : undefined, [sourceOpinions]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={close}
            closeOnOverlayClick
            showConfirm={false}
            closeLabel={selectedIds && selectedIds.length ? 'Close' : 'Cancel'}
        >
            <ModalHeader
                label='Base Opinions'
                icon={Opinion}
            />
            <ArkTable
                colDefs={colDefs}
                rowData={sourceOpinions}
                testId=''
                page={pageNumber}
                total={total}
                next={nextPage}
                previous={previousPage}
                clearAllFilters={clearAllFilters}
                filters={filters}
                updateFilter={updateFilter}
                isLoading={isLoading}
                columnSort={columnSort}
                toggleSort={toggleColumnSort}
                pageSize={pageSize}
                updatePageSize={updatePageSize}
                filterDropdownOptions={filterDropdownOptions}
                removeTextFilter={['scope', 'bespoke', 'focus', 'commissionedBy']}
                emptyTableMessage={emptyTableMessage}
            />
        </ConfirmationModal>
    );
};
